import i18n from "i18next";
import { getAntiforgeryToken } from "./antiforgery";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);

const isSafeMethod = (method?: string) => SAFE_METHODS.has((method ?? "GET").toUpperCase());

const shouldSkipAntiforgery = (input: RequestInfo) => {
  const url = typeof input === "string" ? input : input instanceof Request ? input.url : "";
  return url.includes("/api/security/antiforgery-token");
};

/*
 * The UI language first, then the browser's own tags at lower weight. The API reads the
 * language of the emails it sends (sign-up confirmation, password reset) and of a new
 * organization from the first tag, and the organization's country from the first tag that has
 * a region — so this switches the language to the UI's without losing the browser's country.
 * Capped at four tags to stay a CORS-safelisted header (128 bytes).
 */
export const acceptLanguage = (uiLanguage: string) => {
  const browser = typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]);
  const tags = [uiLanguage, ...browser.filter((tag) => tag && tag !== uiLanguage)].slice(0, 4);
  return tags.map((tag, index) => (index === 0 ? tag : `${tag};q=${(1 - index / 10).toFixed(1)}`)).join(", ");
};

// The AbortErrors our own timeout caused. They keep their AbortError name, so callers that swallow
// aborts still do; `isApiFetchTimeout` tells them apart from a caller's own abort or a network error.
const timeoutErrors = new WeakSet<object>();

/** Whether `error` is a request createApiFetch aborted because it outlived its timeout. */
export const isApiFetchTimeout = (error: unknown) =>
  typeof error === "object" && error !== null && timeoutErrors.has(error);

export const createApiFetch = (timeoutMs: number) => {
  return async (input: RequestInfo, init?: RequestInit) => {
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    // Let callers abort the request manually (e.g. a "Stop" button) by passing
    // their own signal — we forward it to our (timeout-owning) controller.
    const externalSignal = init?.signal ?? undefined;
    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort();
      } else {
        externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
      }
    }

    try {
      const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));

      if (!headers.has("Accept-Language") && i18n.language) {
        headers.set("Accept-Language", acceptLanguage(i18n.language));
      }

      if (!isSafeMethod(method) && !headers.has("X-XSRF-TOKEN") && !shouldSkipAntiforgery(input)) {
        const token = await getAntiforgeryToken(timeoutMs);
        headers.set("X-XSRF-TOKEN", token);
      }

      let response: Response;
      try {
        response = await fetch(input, {
          ...init,
          headers,
          credentials: init?.credentials ?? "include",
          signal: controller.signal,
        });
      } catch (error) {
        // Only the request itself: a token fetch that failed never reached the endpoint.
        if (timedOut && typeof error === "object" && error !== null) {
          timeoutErrors.add(error);
        }
        throw error;
      }

      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent("moneylights:auth-expired"));
      }

      return response;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };
};
